import { Button, type ButtonProps } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import Link from 'next/link'
import React from 'react'

import type { Page, Post } from '@/payload-types'

type CMSLinkType = {
  appearance?: 'inline' | ButtonProps['variant']
  children?: React.ReactNode
  className?: string
  label?: string | null
  labelIsSrOnly?: boolean | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages' | 'posts'
    value: Page | Post | string | number
  } | null
  size?: ButtonProps['size'] | null
  type?: 'custom' | 'reference' | null
  url?: string | null
}

export const CMSLink: React.FC<CMSLinkType> = (props) => {
  const {
    type,
    appearance = 'inline',
    children,
    className,
    label,
    labelIsSrOnly,
    newTab,
    reference,
    size: sizeFromProps,
    url,
  } = props

  const href = (() => {
    if (type !== 'reference' || typeof reference?.value !== 'object') return url

    const ref = reference.value

    if (reference.relationTo === 'pages') {
      // Prefer the nested-docs breadcrumb URL (accounts for parent hierarchy)
      const breadcrumbs = (ref as Page).breadcrumbs
      if (breadcrumbs && breadcrumbs.length > 0) {
        const lastCrumb = breadcrumbs[breadcrumbs.length - 1]
        if (lastCrumb?.url) return lastCrumb.url
      }
      // Fallback: flat slug
      return ref.slug ? `/${ref.slug}` : url
    }

    // Posts and other relations: /{relationTo}/{slug}
    return ref.slug ? `/${reference.relationTo}/${ref.slug}` : url
  })()

  if (!href) return null

  const size = appearance === 'link' ? 'clear' : sizeFromProps
  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  const Label = label ? <span className={cn(labelIsSrOnly ? 'sr-only' : '')}>{label}</span> : null

  /* Ensure we don't break any styles set by richText */
  if (appearance === 'inline') {
    return (
      <Link
        aria-label={label || ''}
        className={cn(className)}
        href={href || url || ''}
        {...newTabProps}
      >
        {Label}
        {children && children}
      </Link>
    )
  }

  return (
    <Button asChild className={className} size={size} variant={appearance}>
      <Link
        aria-label={label || ''}
        className={cn(className)}
        href={href || url || ''}
        {...newTabProps}
      >
        {Label}
        {children && children}
      </Link>
    </Button>
  )
}
